import type * as Types from '@aeppic/types'
import type Aeppic from './aeppic.js'
import { NavigationLookup, type NavigationLookupResult, type NavigationLookupEntry, type NavigationLookupChildren } from './lookup/navigation-lookup.js'
import { useServerLookup } from './lookup/client-lookup.js'

/**
 * @public
 */
export interface NavigationTarget {
  id: string
  label?: string
  appId?: string
  navId?: string
  scopeId?: string
  source: 'application-menu' | string
  root?: boolean
  childrenComplete?: boolean
}

export type NavigationWatchCallback = () => void

/**
 * @public
 */
export class Navigation {
  private _providers = new Map<string, NavigationTarget[]>()
  private _watchers = new Set<NavigationWatchCallback>()
  private _lookups = new Map<string, Promise<NavigationLookupResult>>()
  private _childLookups = new Map<string, Promise<NavigationLookupChildren>>()

  constructor(private _aeppic?: Aeppic) {}

  /** The primary document selects the workspace; sibling pages share one tab-lifetime lookup. */
  async forDocument(identifier: Types.IdOrReference | Types.Document): Promise<NavigationLookupResult> {
    if (!this._aeppic) throw new Error('Navigation lookup requires an Aeppic instance')
    const document = typeof identifier === 'object' && 'a' in identifier
      ? identifier as Types.Document
      : await this._aeppic.get(identifier)
    if (!document) throw new Error('Navigation document not found')
    return this.forRoot(document.a?.[0] || document.id)
  }

  async forRoot(rootId: string): Promise<NavigationLookupResult> {
    if (!this._aeppic) throw new Error('Navigation lookup requires an Aeppic instance')
    if (typeof rootId !== 'string' || !rootId.trim()) throw new TypeError('Missing navigation root')
    rootId = rootId.trim()
    const remote = useServerLookup(this._aeppic, 'navigation')
    const key = JSON.stringify([
      this._aeppic.Account?.id ?? null, rootId, remote, this._aeppic.LocalModel?.enabled === true,
    ])
    let pending = this._lookups.get(key)
    if (!pending) {
      pending = this._resolveRoot(rootId, remote).catch(error => {
        if (this._lookups.get(key) === pending) this._lookups.delete(key)
        throw error
      })
      this._lookups.set(key, pending)
    }
    // Rendering may add expansion/selection state; never mutate the cached snapshot.
    return JSON.parse(JSON.stringify(await pending))
  }

  clearLookupCache(): void {
    this._lookups.clear()
  }

  /** Cached children can render immediately; dynamic collections remain open to discovery. */
  async refreshChildren(rootId: string, applicationId: string, entry: NavigationLookupEntry): Promise<NavigationLookupChildren> {
    if (!this._aeppic) throw new Error('Navigation lookup requires an Aeppic instance')
    if (entry.childrenComplete === true) {
      return JSON.parse(JSON.stringify({ children: entry.children, childrenComplete: true }))
    }
    return this._refreshNavigation(rootId, applicationId, entry.entry.id)
  }

  /** Refresh top-level query sources too, including those with no cached entries. */
  async refreshApplication(rootId: string, applicationId: string): Promise<NavigationLookupChildren> {
    if (!this._aeppic) throw new Error('Navigation lookup requires an Aeppic instance')
    return this._refreshNavigation(rootId, applicationId, null)
  }

  private async _refreshNavigation(rootId: string, applicationId: string, entryId: string | null): Promise<NavigationLookupChildren> {
    const key = JSON.stringify([this._aeppic.Account?.id ?? null, rootId, applicationId, entryId, this._aeppic.LocalModel?.enabled === true])
    let pending = this._childLookups.get(key)
    if (!pending) {
      const lookup = new NavigationLookup(this._aeppic)
      const resolution = entryId === null
        ? lookup.resolveApplication(rootId, applicationId)
        : lookup.resolveChildren(rootId, applicationId, entryId)
      pending = resolution.finally(() => this._childLookups.delete(key))
      this._childLookups.set(key, pending)
    }
    return JSON.parse(JSON.stringify(await pending))
  }

  private async _resolveRoot(rootId: string, remote: boolean): Promise<NavigationLookupResult> {
    if (!remote) return new NavigationLookup(this._aeppic).resolve(rootId)
    const response = await this._aeppic.fetch(`/api/navigation/${encodeURIComponent(rootId)}`)
    if (!response.ok) throw new Error(`Navigation lookup failed: ${response.status}`)
    return response.json()
  }

  registerProvider(providerId: string, snapshot: NavigationTarget[]): () => void {
    this.replaceProviderTargets(providerId, snapshot)

    return () => {
      if (!this._providers.has(providerId)) {
        return
      }

      this._providers.delete(providerId)
      this._emitChanged()
    }
  }

  replaceProviderTargets(providerId: string, targets: NavigationTarget[]): void {
    const normalizedTargets = this._normalizeTargets(targets)
    this._providers.set(providerId, normalizedTargets)
    this._emitChanged()
  }

  isNavigationTarget(documentId: string): boolean {
    return this.getTarget(documentId) !== null
  }

  getTarget(documentId: string): NavigationTarget | null {
    const normalizedId = this._normalizeId(documentId)

    if (!normalizedId) {
      return null
    }

    for (const targets of this._providers.values()) {
      const target = targets.find(candidate => candidate.id === normalizedId)

      if (target) {
        return { ...target }
      }
    }

    return null
  }

  getTargets(): NavigationTarget[] {
    const targets: NavigationTarget[] = []

    for (const providerTargets of this._providers.values()) {
      targets.push(...providerTargets.map(target => ({ ...target })))
    }

    return targets
  }

  watch(callback: NavigationWatchCallback): () => void {
    this._watchers.add(callback)

    return () => {
      this._watchers.delete(callback)
    }
  }

  private _normalizeTargets(targets: NavigationTarget[]): NavigationTarget[] {
    if (!Array.isArray(targets)) {
      return []
    }

    const normalizedTargets: NavigationTarget[] = []

    for (const target of targets) {
      const id = this._normalizeId(target?.id)
      const source = this._normalizeSource(target?.source)

      if (!id || !source) {
        continue
      }

      normalizedTargets.push({
        ...target,
        id,
        source,
      })
    }

    return normalizedTargets
  }

  private _normalizeId(id: string): string {
    return typeof id === 'string' ? id.trim() : ''
  }

  private _normalizeSource(source: string): string {
    return typeof source === 'string' ? source.trim() : ''
  }

  private _emitChanged() {
    for (const watcher of Array.from(this._watchers)) {
      watcher()
    }
  }
}
